import { useQueries } from '@tanstack/react-query'
import { InferenceAPI } from '@/services/utils'
import { GoogleDriveMetadata } from '@/services/inference.gen'

type StorageVolumeInfo = {
  id: number
  storage_volume_id: number
  mount_path: string
}

type EnhancedVolumeInfo = StorageVolumeInfo & {
  volume_name?: string
  size_gb?: number
  google_drive_metadata?: GoogleDriveMetadata[]
  isLoading: boolean
}

export const useStorageVolumeInfo = (
  storage_volume_info?: StorageVolumeInfo[]
): EnhancedVolumeInfo[] => {
  // Use useQueries to fetch multiple volumes in parallel - pass empty array if no volumes
  const volumeQueries = useQueries({
    queries: (storage_volume_info || []).map((volume) => ({
      queryKey: ['storage-volume', volume.storage_volume_id],
      queryFn: async () => {
        const response = await InferenceAPI.getStorageVolumeInfo({
          volume_id: volume.storage_volume_id,
        })
        return response.volume
      },
      // Prevent unnecessary refetching if volume info isn't likely to change
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!storage_volume_info?.length,
    })),
  })

  // Return empty array if no volumes
  if (!storage_volume_info?.length) {
    return []
  }

  // Map queries to the expected format
  return storage_volume_info.map((volume, index) => {
    const query = volumeQueries[index]
    return {
      ...volume,
      volume_name: query.data?.volume_name,
      size_gb: query.data?.size_gb,
      google_drive_metadata: query.data?.google_drive_metadata as
        | GoogleDriveMetadata[]
        | undefined,
      isLoading: query.isLoading,
    }
  })
}
